import { ArrowRight } from 'lucide-react';
import useInView from '../hooks/useInView';

const CASE_STUDIES = [
  {
    tag: 'Fintech',
    title: 'Scaling a Fintech Engineering Team in the U.S.',
    description: 'A high-growth payments company needed to build a core engineering and product team from scratch in under 30 days. We ran a parallel search across 4 roles simultaneously, delivering insight-backed candidates with full funnel management.',
    metrics: [
      { value: '< 30 days', label: 'Time to First Offer' },
      { value: '4 roles', label: 'Hired in Parallel' },
      { value: 'High', label: 'Offer Acceptance Rate' },
    ],
    image: 'https://images.unsplash.com/photo-1687400848467-ac0dca25b2ae?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    tag: 'Product Tech',
    title: 'Executive Search for a B2B SaaS Series B',
    description: 'A fast-scaling SaaS company needed a VP of Engineering and Head of Product within a tight timeline. Through retained search, structured evaluation, and CXO-level alignment, we closed both roles with candidates who are still driving growth.',
    metrics: [
      { value: '2 leaders', label: 'Executive Roles Filled' },
      { value: '45 days', label: 'From Brief to Offer' },
      { value: '100%', label: 'Retention at 12 Months' },
    ],
    image: 'https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    tag: 'Life Sciences',
    title: 'Building a US Commercial Team for a Biotech Startup',
    description: 'A clinical-stage biotech expanding into the US market needed senior commercial talent with rare domain expertise. We leveraged our life sciences network and candidate intelligence framework to identify and place three key hires.',
    metrics: [
      { value: '3 hires', label: 'Senior Commercial Roles' },
      { value: 'Niche', label: 'Domain Expertise Required' },
      { value: '90%', label: 'Candidate Satisfaction Score' },
    ],
    image: 'https://images.unsplash.com/photo-1573165662973-4ab3cf3d3508?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    tag: 'BFSI',
    title: 'Contingency Search for a Lending Platform',
    description: 'A digital lending startup needed Risk, Product, and Operations leaders simultaneously during a period of rapid growth. We delivered a structured shortlist within one week, with detailed candidate intelligence reports on each submission.',
    metrics: [
      { value: '1 week', label: 'First Shortlist Delivered' },
      { value: '3 domains', label: 'Parallel Searches Run' },
      { value: 'Full', label: 'Funnel Visibility Provided' },
    ],
    image: 'https://images.unsplash.com/photo-1746021535489-00edc5efb203?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
];

export default function CaseStudiesSection() {
  const [ref, inView] = useInView();

  return (
    <section id="case-studies" className="py-24 bg-[#0A1628]" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-2 items-end gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#E8500A]" />
              <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
                Our Work
              </span>
            </div>
            <h2
              className="text-4xl sm:text-5xl font-black text-white leading-none tracking-tight"
              style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              data-testid="case-studies-heading"
            >
              Results That Speak<br />for Themselves.
            </h2>
          </div>
          <p className="text-white/40 text-base leading-relaxed lg:text-right" style={{ fontFamily: '"Inter",sans-serif' }}>
            A selection of hiring outcomes across our core industries. Names are anonymized by default.
          </p>
        </div>

        {/* Grid of 4 case studies */}
        <div ref={ref} className="grid md:grid-cols-2 gap-px bg-white/10">
          {CASE_STUDIES.map((cs, i) => (
            <div
              key={i}
              className="group bg-[#0A1628] hover:bg-[#111f38] transition-all duration-300 overflow-hidden"
              style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.65s ease ${i * 120}ms` }}
              data-testid={`case-study-${i}`}
            >
              {/* Image with overlay */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={cs.image}
                  alt={cs.title}
                  className="w-full h-full object-cover grayscale opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#E8500A] text-white text-xs font-bold uppercase tracking-wider px-3 py-1" style={{ fontFamily: '"Inter",sans-serif' }}>
                    {cs.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3
                  className="text-lg font-black text-white mb-3 leading-tight"
                  style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                >
                  {cs.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed mb-6" style={{ fontFamily: '"Inter",sans-serif' }}>
                  {cs.description}
                </p>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                  {cs.metrics.map((m, j) => (
                    <div key={j}>
                      <div className="text-base font-black text-[#E8500A]" style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}>
                        {m.value}
                      </div>
                      <div className="text-xs text-white/30 mt-0.5" style={{ fontFamily: '"Inter",sans-serif' }}>
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
